import { useCallback, useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Lock } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { cn } from "@/lib/utils";
import { Card, CardBody, CardHeader } from "@/components/ui/Card";
import { PageTitleWithHelp, CardTitleWithHelp } from "@/components/ui/title-help";
import { formatNumber } from "@/lib/mrp";
import { buildSalesOrderOverviewPath } from "@/lib/hubEntityDetailPaths";
import {
  TlaPlanningTableHeaderWithHelp,
  formatTlaPlanningDate,
  TlaPlanningPoWithShadowLabel,
  TLA_PLANNING_NUM_CELL_CLASS,
  type TlaPlanningDemandDetailRow,
  type TlaPlanningSupplyDetailRow,
} from "./tlaPlanningShared";
import {
  KodiakLineCompleteLockContextMenuPanel,
  type KodiakLineCompleteLockContextMenuState,
} from "./kodiakTlaPcbaMatchUi";

const DETAIL_PAGE_HOVER_HINT =
  "Supply and demand line items for one TLA planning overview row — TLA build orders on purchase orders versus open sales-order and batch demand at this site.";

const SUPPLY_HOVER_HINT =
  "TLA build orders on purchase orders for the selected SKU and site. Allocated counts serial_fci rows linked through tla_build_order_id.";

const DEMAND_HOVER_HINT =
  "Open demand for the selected SKU and site: batched lines still needing serial allocation, plus unassigned sales order quantity not yet on a batch.";

const SUPPLY_COL_WORK_ORDER_HINT =
  "Megaventory build order number for this TLA purchase-order line.";

const SUPPLY_COL_PURCHASE_ORDER_HINT =
  "Parent purchase order that created this TLA build order.";

const SUPPLY_COL_ORDERED_HINT =
  "Received quantity on this work order: the greater of the purchase-order paper receipt and the work-order hub received quantity.";

const SUPPLY_COL_ALLOCATED_HINT =
  "Devices already linked to this build order through serial_fci.tla_build_order_id.";

const SUPPLY_COL_REMAINING_HINT =
  "Received minus allocated — open build capacity on this work order. MRP admin: right-click to count the work order as fully assigned (count_as_assigned), which zeros remaining without waiting for serial_fci links.";

const SUPPLY_COL_SUBMITTED_HINT = "Date the build order was submitted to the contract manufacturer.";

const SUPPLY_COL_EARLIEST_ASSEMBLED_HINT =
  "Earliest serial_fci assembly date allocated to this build order.";

const SUPPLY_COL_LATEST_ASSEMBLED_HINT =
  "Latest serial_fci assembly date allocated to this build order.";

const DEMAND_COL_SALES_ORDER_HINT = "Open sales order driving this TLA requirement.";

const DEMAND_COL_FINISHED_SKU_HINT =
  "Finished good on the sales order line; BoM topLevel quantity determines TLA need per unit.";

const DEMAND_COL_SOURCE_HINT =
  "batch = quantity on a manufacturing batch; unassigned = sales order quantity not yet batched.";

const DEMAND_COL_BATCH_HINT =
  "Manufacturing batch id when source is batch; blank for unassigned lines.";

const DEMAND_COL_WORK_ORDER_HINT =
  "Build order on the batch item when assigned; blank for unassigned demand.";

const DEMAND_COL_BATCH_QUANTITY_HINT =
  "Units on the batch line, or unbatched sales order quantity for unassigned lines.";

const DEMAND_COL_BOM_QUANTITY_HINT =
  "TLA units required per finished good from create_bom_current topLevel BoM.";

const DEMAND_COL_TLA_NEEDED_HINT = "Batch quantity times BoM quantity — total TLA requirement for this line.";

const DEMAND_COL_FULFILLED_HINT = "serial_fci rows already allocated on the batch line.";

const DEMAND_COL_PENDING_HINT = "TLA still needed after fulfilled allocations.";

const DEMAND_COL_SHIP_BY_HINT = "Sales order SGS ship-by date for prioritization.";

interface TlaPlanningCapacityRow {
  effective_location_id: number;
  location_name: string;
  sku: string;
}

function parseDetailParams(searchParams: URLSearchParams): {
  sku: string | null;
  locationId: number | null;
} {
  const skuRaw = searchParams.get("sku")?.trim() ?? "";
  const sku = skuRaw === "" ? null : skuRaw;
  const locationParsed = Number.parseInt(searchParams.get("location_id") ?? "", 10);
  const locationId = Number.isFinite(locationParsed) && locationParsed > 0 ? locationParsed : null;
  return { sku, locationId };
}

export function TlaPlanningDetailPage() {
  const [searchParams] = useSearchParams();
  const { sku, locationId } = parseDetailParams(searchParams);

  const capacityQuery = useQuery({
    queryKey: ["mrp", "tla-planning", "capacity"],
    queryFn: () =>
      apiFetch<{ rows: TlaPlanningCapacityRow[] }>("/api/mrp/tla-planning/capacity", {
        query: { background: "1" },
      }),
    enabled: sku != null && locationId != null,
  });

  const locationName = useMemo(() => {
    if (sku == null || locationId == null || capacityQuery.data == null) {
      return null;
    }
    const row = capacityQuery.data.rows.find(
      (entry) => entry.sku === sku && Number(entry.effective_location_id) === locationId,
    );
    return row?.location_name ?? `Location ${locationId}`;
  }, [capacityQuery.data, sku, locationId]);

  const supplyQuery = useQuery({
    queryKey: ["mrp", "tla-planning", "supply-detail", sku, locationId],
    queryFn: () =>
      apiFetch<{ rows: TlaPlanningSupplyDetailRow[] }>("/api/mrp/tla-planning/supply-detail", {
        query: { sku: sku!, location_id: String(locationId) },
      }),
    enabled: sku != null && locationId != null,
  });

  const demandQuery = useQuery({
    queryKey: ["mrp", "tla-planning", "demand-detail", sku, locationId],
    queryFn: () =>
      apiFetch<{ rows: TlaPlanningDemandDetailRow[] }>("/api/mrp/tla-planning/demand-detail", {
        query: { sku: sku!, location_id: String(locationId) },
      }),
    enabled: sku != null && locationId != null,
  });

  const queryClient = useQueryClient();
  const hubMrpAdminAllowed = useHubModuleAdmin("mrp");
  const [assignedMenu, setAssignedMenu] = useState<KodiakLineCompleteLockContextMenuState>(null);

  const countAsAssignedMutation = useMutation({
    mutationFn: async (input: { tla_build_order_id: number; lock: boolean }) => {
      if (input.lock) {
        return apiFetch<{ ok: true; tla_build_order_id: number }>(
          "/api/mrp/tla-planning/kodiak-tla-line-complete-lock",
          {
            method: "PUT",
            body: { tla_build_order_id: input.tla_build_order_id },
          },
        );
      }
      const params = new URLSearchParams({
        tla_build_order_id: String(input.tla_build_order_id),
      });
      return apiFetch<{ ok: true; tla_build_order_id: number }>(
        `/api/mrp/tla-planning/kodiak-tla-line-complete-lock?${params.toString()}`,
        { method: "DELETE" },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["mrp", "tla-planning"] });
    },
  });

  const closeAssignedMenu = useCallback(() => {
    setAssignedMenu(null);
  }, []);

  const pendingAssignedBuildOrderId =
    countAsAssignedMutation.isPending && countAsAssignedMutation.variables != null
      ? countAsAssignedMutation.variables.tla_build_order_id
      : null;

  if (sku == null || locationId == null) {
    return <Navigate to="/mrp/tla-planning" replace />;
  }

  const breadcrumbLabel =
    locationName != null ? `${locationName} — ${sku}` : sku;

  const supplyRows = supplyQuery.data?.rows ?? [];
  const demandRows = demandQuery.data?.rows ?? [];
  const detailLoading = supplyQuery.isLoading || demandQuery.isLoading;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={DETAIL_PAGE_HOVER_HINT} breadcrumbLabel={breadcrumbLabel}>
        TLA planning detail
      </PageTitleWithHelp>

      <p className="text-sm text-slate-600">
        <Link className="text-sky-700 hover:text-sky-900" to="/mrp/tla-planning">
          TLA planning
        </Link>
        <span className="text-slate-400"> / </span>
        <span className="font-medium text-slate-800">{breadcrumbLabel}</span>
      </p>

      {detailLoading ? (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Loading supply and demand…
        </p>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitleWithHelp hoverHint={SUPPLY_HOVER_HINT}>Supply</CardTitleWithHelp>
          </CardHeader>
          <CardBody className="space-y-3">
            {supplyQuery.error instanceof Error ? (
              <p className="text-sm text-red-600">{supplyQuery.error.message}</p>
            ) : null}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <TlaPlanningTableHeaderWithHelp
                      label="Work order"
                      hoverHint={SUPPLY_COL_WORK_ORDER_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Purchase order"
                      hoverHint={SUPPLY_COL_PURCHASE_ORDER_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Ordered"
                      hoverHint={SUPPLY_COL_ORDERED_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Allocated"
                      hoverHint={SUPPLY_COL_ALLOCATED_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Remaining"
                      hoverHint={SUPPLY_COL_REMAINING_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Submitted"
                      hoverHint={SUPPLY_COL_SUBMITTED_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Earliest assembled"
                      hoverHint={SUPPLY_COL_EARLIEST_ASSEMBLED_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Latest assembled"
                      hoverHint={SUPPLY_COL_LATEST_ASSEMBLED_HINT}
                      className="px-3 py-2"
                    />
                  </tr>
                </thead>
                <tbody>
                  {supplyRows.map((row, index) => (
                    <tr key={`${row.purchase_order}:${row.mv_number ?? index}`} className="border-b border-slate-100">
                      <td className="px-3 py-2 tabular-nums">{row.mv_number ?? "—"}</td>
                      <td className="px-3 py-2">
                        <TlaPlanningPoWithShadowLabel
                          purchaseOrder={row.purchase_order}
                          isShadowReconciliation={row.is_shadow_reconciliation}
                        />
                      </td>
                      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.ordered)}</td>
                      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.allocated)}</td>
                      <td
                        className={cn(
                          TLA_PLANNING_NUM_CELL_CLASS,
                          hubMrpAdminAllowed && row.build_order_id != null
                            ? "cursor-context-menu"
                            : undefined,
                        )}
                        onContextMenu={(event) => {
                          if (!hubMrpAdminAllowed || row.build_order_id == null) return;
                          event.preventDefault();
                          setAssignedMenu({
                            clientX: event.clientX,
                            clientY: event.clientY,
                            tlaBuildOrderId: row.build_order_id,
                            locked: row.count_as_assigned === true,
                          });
                        }}
                      >
                        <span className="inline-flex items-center justify-end gap-1">
                          {row.count_as_assigned === true ? (
                            <Lock className="h-3.5 w-3.5 shrink-0 text-red-600" aria-label="Fully assigned" />
                          ) : null}
                          {formatNumber(row.remaining)}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums">{formatTlaPlanningDate(row.submitted_date)}</td>
                      <td className="px-3 py-2 tabular-nums">{formatTlaPlanningDate(row.earliest_assembled)}</td>
                      <td className="px-3 py-2 tabular-nums">{formatTlaPlanningDate(row.latest_assembled)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!supplyQuery.isLoading && supplyRows.length === 0 ? (
              <p className="text-sm text-slate-500">No TLA build orders for this SKU and site.</p>
            ) : null}
            <KodiakLineCompleteLockContextMenuPanel
              menu={assignedMenu}
              pendingBuildOrderId={pendingAssignedBuildOrderId}
              onClose={closeAssignedMenu}
              onToggleLock={(tlaBuildOrderId, lock) => {
                closeAssignedMenu();
                countAsAssignedMutation.mutate({ tla_build_order_id: tlaBuildOrderId, lock });
              }}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitleWithHelp hoverHint={DEMAND_HOVER_HINT}>Demand</CardTitleWithHelp>
          </CardHeader>
          <CardBody className="space-y-3">
            {demandQuery.error instanceof Error ? (
              <p className="text-sm text-red-600">{demandQuery.error.message}</p>
            ) : null}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-slate-500">
                    <TlaPlanningTableHeaderWithHelp
                      label="Sales order"
                      hoverHint={DEMAND_COL_SALES_ORDER_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Finished SKU"
                      hoverHint={DEMAND_COL_FINISHED_SKU_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Source"
                      hoverHint={DEMAND_COL_SOURCE_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Batch"
                      hoverHint={DEMAND_COL_BATCH_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Work order"
                      hoverHint={DEMAND_COL_WORK_ORDER_HINT}
                      className="px-3 py-2"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Batch quantity"
                      hoverHint={DEMAND_COL_BATCH_QUANTITY_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="BoM quantity"
                      hoverHint={DEMAND_COL_BOM_QUANTITY_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="TLA needed"
                      hoverHint={DEMAND_COL_TLA_NEEDED_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Fulfilled"
                      hoverHint={DEMAND_COL_FULFILLED_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Pending"
                      hoverHint={DEMAND_COL_PENDING_HINT}
                      className={TLA_PLANNING_NUM_CELL_CLASS}
                      align="right"
                    />
                    <TlaPlanningTableHeaderWithHelp
                      label="Ship by"
                      hoverHint={DEMAND_COL_SHIP_BY_HINT}
                      className="px-3 py-2"
                    />
                  </tr>
                </thead>
                <tbody>
                  {demandRows.map((row, index) => (
                    <tr
                      key={`${row.sales_order_number}:${row.batch_id ?? "u"}:${row.finished_sku}:${index}`}
                      className="border-b border-slate-100"
                    >
                      <td className="px-3 py-2">
                        <Link
                          className="text-brand-700 hover:underline"
                          to={buildSalesOrderOverviewPath(row.sales_order_number)}
                        >
                          {row.sales_order_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2">{row.finished_sku}</td>
                      <td className="px-3 py-2 capitalize">{row.source}</td>
                      <td className="px-3 py-2 tabular-nums">{row.batch_id ?? "—"}</td>
                      <td className="px-3 py-2 tabular-nums">{row.work_order_number ?? "—"}</td>
                      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.batch_quantity)}</td>
                      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.bom_quantity)}</td>
                      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.tla_needed)}</td>
                      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.fulfilled)}</td>
                      <td className={TLA_PLANNING_NUM_CELL_CLASS}>{formatNumber(row.pending)}</td>
                      <td className="px-3 py-2 tabular-nums">{formatTlaPlanningDate(row.sgs_ship_by_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!demandQuery.isLoading && demandRows.length === 0 ? (
              <p className="text-sm text-slate-500">No pending demand for this SKU and site.</p>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
